import { useState, type CSSProperties } from "react";
import { AgeBadge } from "@/components/AgeBadge";
import { EncrelioLogo } from "@/components/Logo";
import { brandVariables } from "@/lib/brand";
import { formatEUR } from "@/lib/format";
import { cn } from "@/lib/utils";
import { BRAND } from "../brand";
import { DEMO_CLIENTS } from "./demoData";

type DemoBrand = {
  name: string;
  /** Libellé du bouton (la marque par défaut le précise). */
  label: string;
  /** Tuile du logo fictif : initiale et forme. Absente = logo par défaut. */
  mark?: { initial: string; shape: string; serif?: boolean };
  primary?: string;
  accent?: string;
};

/**
 * Cabinets fictifs, chacun avec son nom, son logo et ses deux couleurs
 * (tokens.css, palette-demo-*, contrastes vérifiés) : c'est ce que la marque
 * blanche change dans l'application.
 */
const BRANDS: DemoBrand[] = [
  { name: BRAND, label: `${BRAND}, par défaut` },
  {
    name: "Cabinet Lorval",
    label: "Cabinet Lorval",
    mark: { initial: "L", shape: "rounded-md" },
    primary: "var(--palette-demo-sapin-900)",
    accent: "var(--palette-demo-sapin-600)",
  },
  {
    name: "Fiduciaire Bramont",
    label: "Fiduciaire Bramont",
    mark: { initial: "B", shape: "rounded-full" },
    primary: "var(--palette-demo-marine-900)",
    accent: "var(--palette-demo-rouille-600)",
  },
  {
    name: "Comptoir Aveline",
    label: "Comptoir Aveline",
    mark: { initial: "A", shape: "rounded-[10px_3px]", serif: true },
    primary: "var(--palette-demo-bordeaux-900)",
    accent: "var(--palette-demo-framboise-600)",
  },
];

const ROWS = DEMO_CLIENTS.filter((c) =>
  ["arbelot", "monts", "vauban"].includes(c.id),
);
const TABS = ["À relancer", "Clients", "Groupes"];
const RESKIN = "transition-[background-color,color,border-color] duration-300";

/** Logo fictif d'un cabinet : sa tuile et son nom. */
function DemoMark({ brand }: { brand: DemoBrand }) {
  if (!brand.mark) return <EncrelioLogo height={22} inverted />;
  const { initial, shape, serif } = brand.mark;
  return (
    <span role="img" aria-label={brand.name} className="enc-settle inline-flex items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          "grid size-[22px] place-items-center bg-primary-foreground text-[13px] font-bold text-primary",
          RESKIN,
          shape,
          serif && "font-serif",
        )}
      >
        {initial}
      </span>
      <span aria-hidden="true" className="text-sm font-semibold tracking-[-0.01em]">
        {brand.name}
      </span>
    </span>
  );
}

/**
 * La même application sous trois autres marques : le nom, le logo, la marque
 * et l'action changent ; les repères de retard ne bougent pas.
 */
export function WhiteLabelDemo() {
  const [picked, setPicked] = useState(0);
  const brand = BRANDS[picked];
  const vars = brandVariables(brand.primary, brand.accent) as CSSProperties;

  return (
    <div>
      <div role="group" aria-label="Marques de la démonstration" className="flex flex-wrap gap-1.5">
        {BRANDS.map((b, i) => (
          <button
            key={b.name}
            type="button"
            aria-pressed={picked === i}
            onClick={() => setPicked(i)}
            className={cn(
              "inline-flex h-10 cursor-pointer items-center gap-2 rounded-full border px-2.5 text-ui font-semibold transition-[background-color,border-color,color,scale] duration-150 active:scale-[0.97] active:duration-[70ms]",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
              picked === i
                ? "border-primary-foreground bg-primary-foreground text-primary"
                : "border-primary-foreground-subtle/40 text-primary-foreground hover:border-primary-foreground",
            )}
          >
            <span aria-hidden="true" className="flex -space-x-1">
              <span
                className="size-3.5 rounded-full border border-primary-foreground/60"
                style={{ background: b.primary ?? "var(--palette-encre-900)" }}
              />
              <span
                className="size-3.5 rounded-full border border-primary-foreground/60"
                style={{ background: b.accent ?? "var(--palette-violet-600)" }}
              />
            </span>
            {b.label}
          </button>
        ))}
      </div>

      <div
        className="brand-scope mt-6 overflow-hidden rounded-panel border border-primary-foreground/15 bg-surface text-foreground shadow-pop"
        style={vars}
      >
        <div
          data-testid="brand-header"
          className={cn("flex flex-wrap items-center gap-x-6 gap-y-2 bg-primary px-4 py-3 text-primary-foreground", RESKIN)}
        >
          {/* key : le logo du cabinet choisi arrive avec un petit rebond */}
          <DemoMark key={brand.name} brand={brand} />
          <nav aria-hidden="true" className="flex gap-4 text-caption font-semibold">
            {TABS.map((tab, i) => (
              <span
                key={tab}
                className={cn(
                  "border-b-2 pb-1",
                  RESKIN,
                  i === 0
                    ? "border-action-on-primary text-primary-foreground"
                    : "border-transparent text-primary-foreground-muted",
                )}
              >
                {tab}
              </span>
            ))}
          </nav>
        </div>
        <ul className="divide-y divide-border">
          {ROWS.map((row) => (
            <li key={row.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{row.name}</p>
                <p className="text-caption text-muted-foreground">
                  {row.invoices} facture{row.invoices > 1 ? "s" : ""} ·{" "}
                  <span className="whitespace-nowrap">{formatEUR(row.amount)}</span>
                </p>
              </div>
              <AgeBadge days={row.days} />
              <span
                aria-hidden="true"
                className={cn("hidden rounded-md bg-action px-3 py-1.5 text-caption font-semibold text-action-foreground sm:inline-block", RESKIN)}
              >
                Relancer
              </span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-3 text-caption text-primary-foreground-muted">
        Cabinets et données fictifs. Les badges de retard ne changent pas de
        couleur&nbsp;: un retard de 74 jours se lit de la même façon, sous
        toutes les marques.
      </p>
    </div>
  );
}
